import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useAdminEvents,
  useAdminEvent,
  useAdminCreateEvent,
  useAdminUpdateEvent,
  useAdminDeleteEvent,
  useAdminRestoreEvent,
} from '../../hooks/useAdmin'
import { getEventType } from '../../components/ui/eventTypes'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { AdminModal, adminFieldClass, toDatetimeLocal, fromDatetimeLocal } from '../../components/admin/AdminModal'
import type { AdminEvent } from '../../types/admin'

const EVENT_TYPES = ['potluck', 'worship', 'study', 'social', 'outdoors', 'kids', 'service'] as const
const STATUSES = ['active', 'cancelled', 'completed'] as const
const VISIBILITIES = ['public', 'unlisted', 'invite_only'] as const
const SOURCES = ['claimed', 'scraped'] as const

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

type EventFormState = {
  title: string
  eventType: string
  description: string
  addressText: string
  locationLat: string
  locationLng: string
  hostedByType: string
  hostedById: string
  visibility: string
  source: string
  status: string
  startsAt: string
  endsAt: string
}

const emptyForm = (): EventFormState => ({
  title: '',
  eventType: 'potluck',
  description: '',
  addressText: '',
  locationLat: '45.52',
  locationLng: '-122.68',
  hostedByType: 'church',
  hostedById: '',
  visibility: 'public',
  source: 'claimed',
  status: 'active',
  startsAt: toDatetimeLocal(new Date(Date.now() + 7 * 86_400_000).toISOString()),
  endsAt: '',
})

function eventToForm(e: AdminEvent, startsAt?: string): EventFormState {
  return {
    title: e.title,
    eventType: e.eventType,
    description: e.description ?? '',
    addressText: e.addressText,
    locationLat: String(e.locationLat),
    locationLng: String(e.locationLng),
    hostedByType: e.hostedByType,
    hostedById: e.hostedById,
    visibility: e.visibility,
    source: e.source,
    status: e.status,
    startsAt: startsAt ? toDatetimeLocal(startsAt) : e.startsAt ? toDatetimeLocal(e.startsAt) : toDatetimeLocal(new Date().toISOString()),
    endsAt: e.endsAt ? toDatetimeLocal(e.endsAt) : '',
  }
}

export default function AdminEventsPage() {
  const [page, setPage] = useState(1)
  const [confirmDelete, setConfirmDelete] = useState<AdminEvent | null>(null)
  const [editingId, setEditingId] = useState<string | 'new' | null>(null)
  const [form, setForm] = useState<EventFormState>(emptyForm)
  const [showDeleted, setShowDeleted] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [eventTypeFilter, setEventTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [sort, setSort] = useState('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const debounceSearch = useCallback((value: string) => {
    const t = setTimeout(() => { setQ(value); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => debounceSearch(searchInput), [searchInput, debounceSearch])

  const { data, isLoading } = useAdminEvents({
    page, showDeleted, q,
    eventType: eventTypeFilter || undefined,
    status: statusFilter || undefined,
    source: sourceFilter || undefined,
    sort, sortDir,
  })
  const { data: detailData } = useAdminEvent(editingId && editingId !== 'new' ? editingId : null)
  const createEvent = useAdminCreateEvent()
  const updateEvent = useAdminUpdateEvent()
  const deleteEvent = useAdminDeleteEvent()
  const restoreEvent = useAdminRestoreEvent()

  const events = data?.data ?? []
  const total = data?.meta.total ?? 0
  const totalPages = Math.ceil(total / 20)

  useEffect(() => {
    if (editingId && editingId !== 'new' && detailData?.data) {
      setForm(eventToForm(detailData.data))
    }
  }, [editingId, detailData?.data])

  function openCreate() {
    setForm(emptyForm())
    setEditingId('new')
  }

  function openEdit(event: AdminEvent) {
    setForm(eventToForm(event))
    setEditingId(event.id)
  }

  function buildPayload() {
    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      eventType: form.eventType,
      description: form.description || undefined,
      addressText: form.addressText.trim(),
      locationLat: parseFloat(form.locationLat),
      locationLng: parseFloat(form.locationLng),
      hostedByType: form.hostedByType,
      hostedById: form.hostedById.trim(),
      visibility: form.visibility,
      source: form.source,
      status: form.status,
      startsAt: fromDatetimeLocal(form.startsAt),
    }
    if (form.endsAt) payload.endsAt = fromDatetimeLocal(form.endsAt)
    return payload
  }

  function handleSave() {
    const payload = buildPayload()
    if (editingId === 'new') {
      createEvent.mutate(payload, { onSuccess: () => setEditingId(null) })
    } else if (editingId) {
      updateEvent.mutate({ id: editingId, ...payload }, { onSuccess: () => setEditingId(null) })
    }
  }

  const isPending = createEvent.isPending || updateEvent.isPending

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="m-0 text-[24px] font-bold">Events</h1>
          <div className="text-[13px] text-muted mt-1">{total} total</div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setShowDeleted((v) => !v); setPage(1) }}
            className={`h-9 px-3.5 rounded-lg border text-[13px] font-semibold cursor-pointer font-sans ${showDeleted ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-line-soft bg-surface text-ink-2'}`}
          >
            {showDeleted ? 'Hide deleted' : 'Show deleted'}
          </button>
          {!showDeleted && (
            <button
              type="button"
              onClick={openCreate}
              className="h-9 px-4 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans"
            >
              + Add event
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          type="search"
          placeholder="Search events…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] text-ink font-sans flex-1 min-w-[180px]"
        />
        <select
          value={eventTypeFilter}
          onChange={(e) => { setEventTypeFilter(e.target.value); setPage(1) }}
          className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] text-ink font-sans cursor-pointer"
        >
          <option value="">All types</option>
          {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
          className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] text-ink font-sans cursor-pointer"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setPage(1) }}
          className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] text-ink font-sans cursor-pointer"
        >
          <option value="">All sources</option>
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={`${sort}:${sortDir}`}
          onChange={(e) => {
            const [s, d] = e.target.value.split(':')
            setSort(s)
            setSortDir(d as 'asc' | 'desc')
            setPage(1)
          }}
          className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] text-ink font-sans cursor-pointer"
        >
          <option value="created_at:desc">Newest first</option>
          <option value="created_at:asc">Oldest first</option>
          <option value="title:asc">Title A–Z</option>
          <option value="title:desc">Title Z–A</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-muted text-[14px] py-10">Loading…</div>
      ) : (
        <div className="bg-surface rounded-[14px] border border-line-soft overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-line-soft bg-s2">
                {['Title', 'Type', 'Host', 'Source', 'Status', 'Created', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold text-ink-2 text-[12px]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => {
                const t = getEventType(e.eventType as Parameters<typeof getEventType>[0])
                return (
                  <tr key={e.id} className={i < events.length - 1 ? 'border-b border-line-soft' : ''}>
                    <td className="px-4 py-3 font-medium max-w-[220px]">
                      <Link
                        to={`/events/${e.id}`}
                        className="text-primary font-semibold no-underline hover:underline truncate block"
                      >
                        {e.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold"
                        style={{ background: t.soft, color: t.color }}
                      >
                        {t.glyph} {t.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-2 text-[12px]">
                      {e.hostedByType}
                      <div className="text-muted truncate max-w-[120px]">{e.hostedById.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3 text-ink-2">{e.source}</td>
                    <td className="px-4 py-3">
                      <span className={`px-[7px] py-0.5 rounded-[5px] text-[11px] font-semibold ${e.status === 'active' ? 'text-[#1e7e34]' : 'bg-s2 text-muted'}`} style={e.status === 'active' ? { background: '#E6F4EA' } : undefined}>
                        {e.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted">{formatDate(e.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {e.deletedAt ? (
                          <button
                            type="button"
                            onClick={() => restoreEvent.mutate(e.id)}
                            disabled={restoreEvent.isPending}
                            className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-good bg-good-soft text-good-deep disabled:opacity-50"
                          >
                            Restore
                          </button>
                        ) : (
                          <>
                            <button type="button" onClick={() => openEdit(e)} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink">
                              Edit
                            </button>
                            <button type="button" onClick={() => setConfirmDelete(e)} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-danger-line bg-danger-soft text-danger-deep">
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40">← Prev</button>
          <span className="text-[13px] text-muted self-center">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40">Next →</button>
        </div>
      )}

      {editingId && (
        <AdminModal
          title={editingId === 'new' ? 'Add event' : 'Edit event'}
          onClose={() => setEditingId(null)}
          wide
          footer={
            <>
              <button type="button" onClick={() => setEditingId(null)} className="px-4 py-2 rounded-lg border border-line-soft bg-surface text-[13px] cursor-pointer font-sans">Cancel</button>
              <button type="button" onClick={handleSave} disabled={isPending || !form.title || !form.hostedById} className="px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50">
                {isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2 col-span-2">
              Title
              <input className={adminFieldClass()} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Type
              <select className={adminFieldClass()} value={form.eventType} onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value }))}>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Status
              <select className={adminFieldClass()} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2 col-span-2">
              Address
              <input className={adminFieldClass()} value={form.addressText} onChange={(e) => setForm((f) => ({ ...f, addressText: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Lat
              <input className={adminFieldClass()} value={form.locationLat} onChange={(e) => setForm((f) => ({ ...f, locationLat: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Lng
              <input className={adminFieldClass()} value={form.locationLng} onChange={(e) => setForm((f) => ({ ...f, locationLng: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Hosted by type
              <select className={adminFieldClass()} value={form.hostedByType} onChange={(e) => setForm((f) => ({ ...f, hostedByType: e.target.value }))}>
                <option value="church">church</option>
                <option value="person">person</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Hosted by ID (UUID)
              <input className={adminFieldClass()} value={form.hostedById} onChange={(e) => setForm((f) => ({ ...f, hostedById: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Visibility
              <select className={adminFieldClass()} value={form.visibility} onChange={(e) => setForm((f) => ({ ...f, visibility: e.target.value }))}>
                {VISIBILITIES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Source
              <select className={adminFieldClass()} value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Starts at
              <input type="datetime-local" className={adminFieldClass()} value={form.startsAt} onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Ends at <span className="font-normal text-muted">(optional)</span>
              <input type="datetime-local" className={adminFieldClass()} value={form.endsAt} onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2 col-span-2">
              Description
              <textarea className={`${adminFieldClass()} min-h-[80px]`} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </label>
          </div>
        </AdminModal>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete event?"
          body={<><strong>{confirmDelete.title}</strong> will be soft-deleted and hidden from public search. It can be restored from the deleted events view.</>}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          onConfirm={() => deleteEvent.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) })}
          onCancel={() => setConfirmDelete(null)}
          isPending={deleteEvent.isPending}
        />
      )}
    </div>
  )
}
